import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { api, type MemberProfile } from "../api";
import { Avatar } from "../components/Avatar";
import { BookCover } from "../components/BookCover";
import { Badge, Card, SectionHeading, Spinner, Tag } from "../components/ui";

interface Pick {
  id: number;
  title: string;
  author: string;
  coverUrl: string | null;
  addedAt: string;
}

export default function MemberDetailPage() {
  const { id } = useParams<{ id: string }>();
  const numericId = Number(id);

  const detail = useQuery({
    queryKey: ["member", numericId],
    queryFn: () =>
      api.get<{ profile: MemberProfile; picks: Pick[] }>(`/members/${numericId}`),
    enabled: !!numericId,
  });

  if (detail.isLoading) return <Spinner />;
  if (!detail.data) return <p className="text-plum-soft">Not found.</p>;

  const { profile, picks } = detail.data;
  const memberSince = format(parseISO(profile.createdAt.replace(" ", "T")), "MMMM yyyy");

  return (
    <div className="flex flex-col gap-12">
      <Link to="/members" className="text-sm text-plum-mute hover:text-rose w-fit">
        ← All members
      </Link>

      <section className="flex flex-col md:flex-row gap-8 items-center md:items-start">
        <Avatar
          name={profile.name}
          email={profile.email}
          hue={profile.avatarHue}
          size="xl"
          className="w-28 h-28 text-4xl"
        />
        <div className="flex-1 text-center md:text-left">
          <h1 className="font-serif text-4xl text-plum tracking-tight">
            {profile.name ?? profile.email.split("@")[0]}
          </h1>
          <p className="text-plum-soft mt-1">{profile.email}</p>
          <p className="text-xs uppercase tracking-[0.18em] text-gold-dark mt-3">
            Member since {memberSince}
          </p>
          {profile.stats?.isCurrentPicker ? (
            <div className="mt-3"><Badge tone="rose">Picking now</Badge></div>
          ) : null}
          {profile.bio ? (
            <p className="font-serif italic text-lg text-plum-soft mt-5 leading-relaxed max-w-prose">
              "{profile.bio}"
            </p>
          ) : null}

          {profile.favoriteGenres.length > 0 ? (
            <div className="flex flex-wrap gap-2 mt-5 justify-center md:justify-start">
              {profile.favoriteGenres.map((g) => (
                <Tag key={g}>{g}</Tag>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      {profile.stats ? (
        <Card accent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <BigStat label="Books picked" value={profile.stats.booksPicked} />
            <BigStat label="Notes left" value={profile.stats.notesLeft} />
            <BigStat label="Ratings given" value={profile.stats.ratingsGiven} />
            <BigStat label="Quotes saved" value={profile.stats.bookmarksSaved} />
          </div>
        </Card>
      ) : null}

      {picks.length > 0 ? (
        <div className="flex flex-col gap-6">
          <SectionHeading eyebrow="Their picks" title="Books they brought to the table" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
            {picks.map((p) => (
              <Link key={p.id} to={`/book/${p.id}`} className="flex flex-col items-center text-center group">
                <BookCover url={p.coverUrl} title={p.title} author={p.author} size="lg" className="group-hover:-translate-y-1" />
                <div className="mt-3 px-1">
                  <h4 className="font-serif text-sm text-plum leading-tight line-clamp-2">{p.title}</h4>
                  <p className="text-plum-mute text-xs italic mt-0.5">{p.author}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function BigStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="text-center">
      <div className="font-serif text-3xl text-plum leading-none">{value}</div>
      <div className="text-[0.7rem] uppercase tracking-[0.16em] text-plum-mute mt-2">{label}</div>
    </div>
  );
}
